import { useFormContext } from 'react-hook-form';
import {
  useFlowsDispatch,
  useFlowsSelector,
} from '../../../../../../../common/src/utils/globalVariables';
import useAnswerForQuestion from '../../../../../hooks/useAnswerForQuestion';
import useFlowsHelper from '../../../../../hooks/useFlowsHelper';
import { useFlowsProgression } from '../../../../../hooks/useFlowsProgression';
import {
  removeAnswerForIndex,
  replaceAnswers,
  reshuffleAnswerIndices,
  updateAnswers,
} from '../../../../../store/redux/slices/flowsSlice';
import {
  FlowsQuestion,
  FlowsQuestionAnswer,
} from '../../../../../types/FlowsTypes';
import getDuplicateQuestionsAnswers from '../utils/getDuplicateQuestionsAnswers';
import useDuplicateGroupIds from './useDuplicateGroupIds';
import useUpdateItemAmount from './useUpdateItemAmount';

type RemoveTableItem = (toBeRemovedIndex?: number) => void;

/**
 * This functions works as follows:
 * - Step 1: The answers need to be cleared from BE
 *   - Prepare a payload where each answer has the value undefined
 *   - Send this payload to BE
 * - Step 2: Remove all answer objects locally
 * - Step 3: Check for any dependant questions and adjust their answer accordingly
 * - Step 4: Replace all indices of questions that have an index higher or equal to the removed index
 * - Step 5: Decrement the amount question to resemble the correct amount
 *
 * @param question The amount question for the TABLE input type
 * @returns A function that mimicks the delete functionality for dynamic duplicate questions
 * @deprecated This should no longer be used. It should only be used for entities that do not have an object in SMT yet
 */
const useRemoveTableItemLegacy = (question: FlowsQuestion): RemoveTableItem => {
  const { getGroupByQuestionId } = useFlowsHelper();
  const { saveFields } = useFlowsProgression();
  const flowsDispatch = useFlowsDispatch();
  const updateItemAmount = useUpdateItemAmount(question);
  const duplicateGroupIds = useDuplicateGroupIds(question.id);
  const flowAnswers = useFlowsSelector((state) => state.flows.answers);
  const getAnswerForQuestion = useAnswerForQuestion();

  // TODO: Make useFlowsAnswers hook
  const { setValue } = useFormContext();

  const itemAmount =
    (getAnswerForQuestion(question.id, question.index)?.answer as number) || 0;

  return (toBeRemovedIndex?: number) => {
    if (toBeRemovedIndex == null) return;

    // -- STEP ONE
    // First we prepare the answers to be cleared
    // We set their answers to null so that BE can receive it
    const answers = getDuplicateQuestionsAnswers(question.id, toBeRemovedIndex);
    const clearedAnswers = answers
      .filter(({ id }) => id !== question.id)
      .map((answer) => ({
        ...answer,
        answer: undefined,
      }));

    // -- STEP TWO
    // We apply the changes to the answers state
    flowsDispatch(
      replaceAnswers({
        answers: clearedAnswers,
        index: toBeRemovedIndex,
      }),
    );

    // -- STEP THREE
    // Remove any answers for dependant questions
    const indexedAnswers: FlowsQuestionAnswer[] = [];
    question.rules.table?.index_question_ids?.forEach((questionId) => {
      const group = getGroupByQuestionId(questionId);
      const indexedAnswer = flowAnswers.find(({ id }) => id === questionId)
        ?.answer as number[];

      const newAnswerValue = indexedAnswer?.filter(
        (index) => index !== toBeRemovedIndex,
      );

      const newIndexedAnswer = {
        id: questionId,
        answer: newAnswerValue,
        groupId: group.id,
        tag: question.tag,
      };

      // TODO: Make useFlowsAnswers hook
      // - This is a temporary measure to save out time
      // - In theory there should be a useFlowsAnswer hook that takes
      // logic away from the useFlowsInputComponent hook
      setValue(questionId, newAnswerValue);
      flowsDispatch(updateAnswers(newIndexedAnswer));
      indexedAnswers.push(newIndexedAnswer);
    });

    // -- STEP FOUR
    // Update the amount and apply all changes to BE
    // Also include the changes to dependant question ids of INDEX input questions
    updateItemAmount(itemAmount - 1);
    const allAnswers = getDuplicateQuestionsAnswers(question.id);
    saveFields([...allAnswers, ...indexedAnswers]);

    // -- STEP FIVE
    // The changes were applied to BE, the answers can now be deleted locally
    duplicateGroupIds?.forEach((duplicateGroupId) => {
      flowsDispatch(
        removeAnswerForIndex({
          forIndex: toBeRemovedIndex,
          groupId: duplicateGroupId,
        }),
      );
    });

    // After all is done, reshuffle indicies
    duplicateGroupIds?.forEach((duplicateGroupId) => {
      flowsDispatch(
        reshuffleAnswerIndices({
          forIndex: toBeRemovedIndex,
          groupId: duplicateGroupId,
        }),
      );
    });
  };
};

export default useRemoveTableItemLegacy;
